import { useQuery } from '@tanstack/react-query';
import { fetchProducts, fetchProductByHandle } from '@/services/shopify';
import { Equipo, Mascarilla } from '@/context/AppContext';

/*
 * MIGRATION NOTICE - Individual Product Hooks
 * ============================================
 * 
 * These hooks are deprecated in favor of kit-based selection.
 * 
 * MIGRATION PATH:
 * Old: useProducts(), useEquipos(), useMascarillas()
 * New: useKitData(maskRecommendation)
 * 
 * REPLACEMENT USAGE:
 * ```typescript
 * // Old approach (DEPRECATED)
 * const { equipos, mascarillas } = useProducts();
 * 
 * // New approach
 * const { recommendedKits } = useKitData(maskRecommendation);
 * ```
 * 
 * REMOVAL TIMELINE:
 * - Phase 1: Add deprecation warnings ✅
 * - Phase 2: Remove from active components ✅  
 * - Phase 3: Remove hooks entirely (future version)
 */

/**
 * @deprecated This interface is for individual product selection which is being phased out.
 * Use useKitData hook instead for kit-based selection.
 * Maintained for backward compatibility only.
 */
export interface UseProductsResult {
  equipos: Equipo[];
  mascarillas: Mascarilla[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch all products (equipos and mascarillas)
 * @deprecated Individual product selection is being phased out in favor of kit-based selection.
 * Use useKitData hook instead for the new kit-based approach.
 * This hook is maintained for backward compatibility with existing quotes only.
 * Will be removed in a future version.
 * @see useKitData for kit-based product selection
 */
export const useProducts = (): UseProductsResult => {
  // Development-only deprecation warning
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      'useProducts hook is deprecated and will be removed in a future version. ' +
      'Please use useKitData hook for kit-based selection instead. ' +
      'This hook is only maintained for backward compatibility.'
    );
  }

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in older versions)
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    equipos: data?.equipos || [],
    mascarillas: data?.mascarillas || [],
    isLoading,
    error: error as Error | null,
    refetch
  };
};

/**
 * Hook to fetch a specific product by Shopify handle
 * @deprecated Individual product selection is being phased out in favor of kit-based selection.
 * Use useKitData hook instead for the new kit-based approach.
 * This hook is maintained for backward compatibility with existing quotes only.
 * Will be removed in a future version.
 * @see useKitData for kit-based product selection
 */
export const useProductByHandle = (handle: string) => {
  return useQuery({
    queryKey: ['product', handle],
    queryFn: () => fetchProductByHandle(handle),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    enabled: !!handle,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to get equipos only
 * @deprecated Individual product selection is being phased out in favor of kit-based selection.
 * Use useKitData hook instead for the new kit-based approach.
 * This hook is maintained for backward compatibility with existing quotes only.
 * Will be removed in a future version.
 * @see useKitData for kit-based product selection
 */
export const useEquipos = () => {
  const { equipos, isLoading, error, refetch } = useProducts();
  return { equipos, isLoading, error, refetch };
};

/**
 * Hook to get mascarillas only
 * @deprecated Individual product selection is being phased out in favor of kit-based selection.
 * Use useKitData hook instead for the new kit-based approach.
 * This hook is maintained for backward compatibility with existing quotes only.
 * Will be removed in a future version.
 * @see useKitData for kit-based product selection
 */
export const useMascarillas = () => {
  const { mascarillas, isLoading, error, refetch } = useProducts();
  return { mascarillas, isLoading, error, refetch };
};